import { queryOptions } from '@tanstack/react-query'
import { report } from '../runtime/report'
import { type AttributionParam, withAttribution } from './attribution'
import {
  type BillingPeriod,
  childPrice,
  DEFAULT_SHOP_DOMAIN,
  type PurchasablePlan,
  variantIds,
} from './catalogue'
import { type DiscountKind, formatPreciseCents, type LineDiscount, lineDiscounts } from './pricing'

/**
 * The handoff: checkout happens on Shopify, and this is the entire interface
 * to it — the tokenless Storefront API, which covers selling plans and cart
 * writes without any access token in the bundle.
 *
 * Subscriptions are Recharge's, and on the Shopify Checkout Integration a
 * Recharge subscription is a Shopify selling plan on the cart line. A variant
 * added without one is a one-time purchase, and cart permalinks cannot carry
 * one, so the handoff is a `cartCreate` instead of a URL:
 *
 *  - one line per child, quantity 1, so each child becomes their own
 *    subscription that can be paused or cancelled alone, with the child's name
 *    as a line attribute that recurs on every renewal order. (Shopify merges
 *    lines that are identical in every way, so two children with the same
 *    name become one line of quantity 2 — the same subscription either way.)
 *  - the plan's variant on the first child's line, and its sibling product's
 *    on every other (`childPrice`): the sibling discount is that product's
 *    lower price, so it recurs on every renewal and a discount code still
 *    applies on top.
 *  - each of the line's discounts as an attribute after the child's name,
 *    such as "10% SIBLING DISCOUNT: -$42.00" (`lineDiscounts`). Shopify shows
 *    nothing of a discount that is part of a price, and no discount at all in
 *    the recurring price, so the line says what was taken off. Like the name,
 *    they recur on every renewal order, a monthly plan's first-kit discount
 *    included, though Recharge takes it off the first charge only.
 *  - the selling plan matching the billing period, resolved at runtime because
 *    Recharge documents plan ids as unstable: fetched once per page for every
 *    catalogue variant (`sellingPlansQuery`) and cached.
 *
 * The line attributes are a provisional contract with fulfillment; they show
 * in checkout, on the order and in the Recharge portal as "Child: …" and so on.
 *
 * The ad and campaign parameters the visitor landed with (`attribution.ts`)
 * ride along twice: as cart attributes, which Shopify keeps on the order, and
 * on the checkout URL, for Shopify's attribution and the shop's ad pixels. A
 * cart refused with the attributes is created again without them.
 */

export const STOREFRONT_API_VERSION = '2026-07'

const CHILD_ATTRIBUTE_KEY = 'Child'

/**
 * What a discount is called on a cart line, after its percent, in capitals like
 * Shopify's own. An annual plan's first kit is discounted every year, unlike a
 * monthly plan's, and its name says so.
 */
function discountName(kind: DiscountKind, plan: PurchasablePlan, billing: BillingPeriod): string {
  const noun = plan.noun.toUpperCase()
  switch (kind) {
    case 'sibling':
      return 'SIBLING DISCOUNT'
    case 'annual':
      return 'ANNUAL DISCOUNT'
    case 'first-kit':
      return billing === 'annual' ? `OFF FIRST ${noun} EACH YEAR` : `FIRST ${noun} DISCOUNT`
  }
}

/** A discount as a line attribute, which Shopify shows as "10% SIBLING DISCOUNT: -$42.00". */
function discountAttribute(
  plan: PurchasablePlan,
  billing: BillingPeriod,
  { kind, percent, cents }: LineDiscount,
) {
  return {
    key: `${percent}% ${discountName(kind, plan, billing)}`,
    value: `-${formatPreciseCents(cents)}`,
  }
}

/** Selling plan id per billing period, keyed by variant GID. */
export type SellingPlans = Record<string, Partial<Record<BillingPeriod, string>>>

export interface CheckoutHandoff {
  /**
   * The shop hostname — or whatever a hand-written `data-shop-domain`
   * attribute delivers. That includes a full pasted URL with a path and query,
   * stray whitespace, and even a boolean (`data-shop-domain=""` coerces to
   * `true` in the prop pipeline). Anything unusable falls back to the
   * production shop rather than producing a broken request.
   */
  shopDomain?: unknown
  plan: PurchasablePlan
  billing: BillingPeriod
  /** One entry per child; each becomes its own cart line. */
  childNames: readonly string[]
  /** The ad and campaign parameters the visitor landed with (`attribution.ts`). */
  attribution?: readonly AttributionParam[]
}

/**
 * Reduce whatever `data-shop-domain` delivered to a bare hostname. Marketing
 * writes that attribute by hand in Webflow, so a pasted storefront URL
 * ("https://shop.example/en-us?srsltid=…") must yield "shop.example" — naive
 * prefix-stripping would splice the API path into that query string, and an
 * empty result would let the URL parser promote "api" to the hostname.
 */
function resolveShopDomain(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_SHOP_DOMAIN
  const trimmed = value.trim()
  if (trimmed === '') return DEFAULT_SHOP_DOMAIN

  try {
    const url = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`)
    return url.hostname || DEFAULT_SHOP_DOMAIN
  } catch {
    return DEFAULT_SHOP_DOMAIN
  }
}

function variantGid(variantId: string): string {
  return `gid://shopify/ProductVariant/${variantId}`
}

interface GraphQLResponse<Data> {
  data?: Data
  errors?: { message: string }[]
}

/** POST one document to the shop's Storefront API and return its data. */
async function storefront<Data>(
  shopDomain: string,
  query: string,
  variables: Record<string, unknown>,
): Promise<Data> {
  const response = await fetch(`https://${shopDomain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  })
  if (!response.ok) throw new Error(`Storefront API responded ${response.status}`)

  const { data, errors } = (await response.json()) as GraphQLResponse<Data>
  if (errors?.length || !data) {
    const detail = errors?.map((error) => error.message).join('; ') || 'no data'
    throw new Error(`Storefront API error: ${detail}`)
  }
  return data
}

const SELLING_PLANS_QUERY = `
  query SellingPlans($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        sellingPlanAllocations(first: 10) {
          nodes {
            sellingPlan {
              id
              billingPolicy {
                ... on SellingPlanRecurringBillingPolicy { interval intervalCount }
              }
            }
          }
        }
      }
    }
  }
`

interface BillingPolicy {
  interval: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'
  intervalCount: number
}

interface VariantNode {
  id: string
  sellingPlanAllocations: { nodes: { sellingPlan: { id: string; billingPolicy: BillingPolicy } }[] }
}

/**
 * Which catalogue billing period a plan's billing policy is, if either.
 * Recharge lets an annual plan be configured as "every 1 year" or "every 12
 * months"; both are annual here.
 */
function periodFor({ interval, intervalCount }: BillingPolicy): BillingPeriod | undefined {
  const months = interval === 'MONTH' ? intervalCount : interval === 'YEAR' ? 12 * intervalCount : 0
  if (months === 1) return 'monthly'
  if (months === 12) return 'annual'
  return undefined
}

async function fetchSellingPlans(shopDomain: string): Promise<SellingPlans> {
  const { nodes } = await storefront<{ nodes: (VariantNode | null)[] }>(
    shopDomain,
    SELLING_PLANS_QUERY,
    { ids: variantIds.map(variantGid) },
  )

  const sellingPlans: SellingPlans = {}
  // Shopify answers an unknown id with null rather than an error.
  for (const variant of nodes.filter((node) => node !== null)) {
    const byPeriod: Partial<Record<BillingPeriod, string>> = {}
    for (const { sellingPlan } of variant.sellingPlanAllocations.nodes) {
      const period = periodFor(sellingPlan.billingPolicy)
      if (period) byPeriod[period] ??= sellingPlan.id
    }
    sellingPlans[variant.id] = byPeriod
  }
  return sellingPlans
}

/**
 * Every catalogue variant's selling plans, fetched once and cached for the
 * page: the Checkout widget prefetches at mount so Continue is normally a
 * single request. Plan ids do not change within a session, hence never stale.
 */
export function sellingPlansQuery(shopDomain: unknown) {
  const domain = resolveShopDomain(shopDomain)
  return queryOptions({
    queryKey: ['shopify', domain, 'selling-plans'],
    queryFn: () => fetchSellingPlans(domain),
    staleTime: Number.POSITIVE_INFINITY,
  })
}

const CART_CREATE_MUTATION = `
  mutation CreateCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart { checkoutUrl }
      userErrors { field message }
    }
  }
`

interface CartCreateData {
  cartCreate: {
    cart: { checkoutUrl: string } | null
    userErrors: { field: string[] | null; message: string }[]
  }
}

/** Create the cart on Shopify and resolve to its checkout URL. */
export async function createCheckout(
  handoff: CheckoutHandoff,
  sellingPlans: SellingPlans,
): Promise<string> {
  const { shopDomain, plan, billing, childNames, attribution = [] } = handoff

  if (childNames.length === 0) {
    throw new Error('checkout requires at least one child')
  }

  const lines = childNames.map((name, index) => {
    const { variantId } = childPrice(plan, billing, index)
    const merchandiseId = variantGid(variantId)
    const sellingPlanId = sellingPlans[merchandiseId]?.[billing]
    if (!sellingPlanId) {
      throw new Error(`no ${billing} selling plan on variant ${variantId}`)
    }
    return {
      merchandiseId,
      quantity: 1,
      sellingPlanId,
      attributes: [
        { key: CHILD_ATTRIBUTE_KEY, value: name.trim() },
        ...lineDiscounts(plan, billing, index).map((discount) =>
          discountAttribute(plan, billing, discount),
        ),
      ],
    }
  })

  const domain = resolveShopDomain(shopDomain)
  const createCart = async (input: Record<string, unknown>) => {
    const { cartCreate } = await storefront<CartCreateData>(domain, CART_CREATE_MUTATION, { input })
    if (!cartCreate.cart || cartCreate.userErrors.length > 0) {
      const detail = cartCreate.userErrors.map((error) => error.message).join('; ')
      throw new Error(`cartCreate rejected: ${detail}`)
    }
    return cartCreate.cart.checkoutUrl
  }

  if (attribution.length === 0) return createCart({ lines })

  let checkoutUrl: string
  try {
    checkoutUrl = await createCart({
      lines,
      attributes: attribution.map(({ key, value }) => ({ key, value })),
    })
  } catch (error) {
    // Attribution must never cost a sale. The same saved set goes with every
    // Continue for weeks, so a cart Shopify refuses over one of these would be
    // refused each time: try once more without them. Reported only when that
    // works, since only then were the attributes to blame.
    checkoutUrl = await createCart({ lines })
    report(error, { component: 'Checkout', level: 'warning' })
  }
  return withAttribution(checkoutUrl, attribution)
}
